import { Navigate, useLocation, useSearchParams } from "react-router";

import { InviteLinkPrompt } from "@/components/auth/InviteLinkPrompt";
import { rememberRedirectAfterAuth } from "@/lib/redirectAfterAuth";
import { useAuthStore } from "@/store/authStore";

/**
 * Route guard for `/app/:appId`. An anonymous visitor following a share link
 * lands on the app itself and is asked to sign in inline, from a bottom sheet
 * over the running app (GOM-263) — bouncing them to `/login` here was what
 * killed the viral loop. The backend still decides what they may see: it serves
 * the app read-only for a public one, or for any app whose `?invite=` code
 * matches, and 403s otherwise.
 *
 * Signed-in callers go through the same gates as any protected page, with one
 * exception: an `?invite=` code stands in for a completed invite flow, since it
 * *is* the credential that's about to grant them access.
 */
export function GuestOrProtectedRoute({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, loading } = useAuthStore();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const inviteCode = searchParams.get("invite");

  // Don't show PageLoader — let AppRuntime handle its own loading to avoid
  // two sequential loading screens (route guard loader → app loader).
  if (loading) {
    return <>{children}</>;
  }

  if (user) {
    if (!user.inviteAccepted) {
      // The code in the URL is the pending credential — let them through and
      // let acceptance finish behind the app.
      return inviteCode ? <>{children}</> : <InviteLinkPrompt />;
    }
    // First-run accounts finish the profile wizard before they land in an app.
    // Everything they touch here is collaborative: an edit fires notifications
    // to the app's other members, stamped with the display name and username
    // the wizard collects. Arriving first and setting them afterwards means
    // that first round of notifications goes out as a blank profile, and
    // nothing back-fills it. `/onboarding` hands them back here when it's done.
    if (!user.onboardingCompleted) {
      rememberRedirectAfterAuth(location.pathname + location.search);
      return <Navigate to="/onboarding" replace />;
    }
    return <>{children}</>;
  }

  // Remembered even though we no longer redirect: the sign-in sheet's OAuth
  // legs leave the page entirely, and this is what brings them back here.
  rememberRedirectAfterAuth(location.pathname + location.search);
  return <>{children}</>;
}
